import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useLoaderData } from 'react-router-dom';
import { axiosClient } from '../../../library/Service/axiosClient';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import Swal from 'sweetalert2';

const UploadRecruiterLayout = (props: any) => {
    const { internal } = useLoaderData() as State;
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false); // Loading state
    const [isQueueInprogress, setIsQueueInprogress] = useState(internal?.userQueueStatus === 1 ? false : true);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false); // Loading state
    const [uploadProgress, setUploadProgress] = useState(0); // Progress bar state
    const [responseMessage, setResponseMessage] = useState<{ processed: number; invalid: number } | null>(null); // Response state
    interface InvalidRecord {
        record: any;
        errors: string[];
    }

    const [invalidData, setInvalidData] = useState<InvalidRecord[]>([]);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (isQueueInprogress) {
            Swal.fire({
                title: 'Queue is in progress...!',
                html: `
                    <div style="display: flex; flex-direction: column; align-items: center;">
                        <div class="swal2-spinner" style="margin-bottom: 16px;">
                            <svg width="40" height="40" viewBox="0 0 40 40">
                                <circle cx="20" cy="20" r="18" stroke="#3085d6" stroke-width="4" fill="none" stroke-linecap="round">
                                    <animate attributeName="stroke-dasharray" values="1,150;90,150;90,150" dur="1.5s" repeatCount="indefinite"/>
                                    <animate attributeName="stroke-dashoffset" values="0;-35;-124" dur="1.5s" repeatCount="indefinite"/>
                                    <animate attributeName="stroke" values="#3085d6;#3fc3ee;#87adbd;#3085d6" dur="6s" repeatCount="indefinite"/>
                                </circle>
                            </svg>
                        </div>
                        <div>Please come back after a while..!!</div>
                    </div>
                `,
                icon: 'warning',
                allowOutsideClick: false,
                showCloseButton: false,
                allowEscapeKey: false,
                showConfirmButton: true,
                confirmButtonText: 'Refresh',
                showDenyButton: true,
                denyButtonText: 'Go Back To Previous Page',
                // No Swal.showLoading() here!
                preConfirm: () => {
                    navigate(0);
                },
                preDeny: () => {
                    navigate(-1);
                },
            });
        } else {
            Swal.close();
        }
        // Cleanup on unmount
        return () => {
            Swal.close();
        };
    }, [isQueueInprogress, isFormSubmitted, navigate, location]);


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!file) {
            console.error('No file selected');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        const url = `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/upload-recruiter/upload-sheet`;

        try {
            setIsLoading(true); // Start loading
            setUploadProgress(0); // Reset progress

            let uploadResponse = await axiosClient().post(url, formData, {
                onUploadProgress: (progressEvent) => {
                    if (progressEvent && progressEvent.loaded !== undefined && progressEvent.total !== undefined) {
                        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        setUploadProgress(progress); // Update progress
                    } else {
                        console.warn('Progress information is missing or incomplete.');
                        setUploadProgress(0); // Default to 0 if progress cannot be calculated
                    }
                },
            });
            
            if (uploadResponse.data.usersInQueue > 0) {
                setIsQueueInprogress(true)
            }
            const { message, invalidRecords, data, invalidData } = uploadResponse.data;
            setInvalidData(invalidData || []);
            // Update response message state
            setResponseMessage({
                processed: data.length,
                invalid: invalidRecords,
            });

            setIsFormSubmitted(true)
            console.log('File uploaded successfully');
        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setIsLoading(false); // End loading
        }
    };

    return (
        <div className="container max-1140 px-lg-0 overflow-hidden">
            <div className="row">
                <div className="col-md-12 mb-4">
                    <div className="container mt-5">
                        <h2 className="mb-4">Upload Recruiter Excel Data</h2>

                        <div className="alert alert-info">
                            <h5>Please Note:</h5>
                            <ul>
                                <li>The file must be in Excel format (.xlsx).</li>
                                <li>
                                <strong>Email</strong> and <strong>Student ID</strong> columns should not be blank.
                                </li>
                                <li>
                                    <strong>Email and Studnet Id</strong> must be valid.
                                </li>
                            </ul>
                            <div className='d-flex align-items-center'>
                                <a href="https://s3.ap-south-1.amazonaws.com/media.univariety.dev/Bulk_Recruiter_Sample_File.xlsx" className="btn btn-green " download="">
                                    <span className='d-flex align-items-center'><i className="bi bi-cloud-arrow-down-fill font-20 me-2"></i> Download Bulk Recruiter Sample File</span>
                                </a>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                            <div className="mb-3">
                                <label htmlFor="fileInput" className="form-label">
                                    Choose file (Only Excel)
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="fileInput"
                                    accept=".xlsx"
                                    onChange={handleFileChange}
                                    required
                                />
                            </div>
                            {!isFormSubmitted && (
                                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                    {isLoading ? 'Uploading...' : 'Upload'}
                                </button>
                            )}
                        </form>
                        {isLoading && (
                            <div className="progress mt-3">
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}>
                                    {uploadProgress}%
                                </div>
                            </div>
                        )}
                        {!isQueueInprogress && responseMessage && (
                            <div className="mt-4">
                                <h5>Upload Summary</h5>
                                <p>
                                    <strong>Valid Records inserted in DB:</strong> {responseMessage.processed}
                                </p>
                                <p>
                                    <strong>Invalid Records:</strong> {responseMessage.invalid}
                                </p>
                            </div>
                        )}
                        {/* Conditionally show invalid data */}
                        {invalidData.length > 0 && (
                            <div>
                                <h3>Invalid Data</h3>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Record</th>
                                            <th scope="col">Errors</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invalidData.map((item, index) => (
                                            <tr key={index}>
                                                <td>{JSON.stringify(item.record)}</td>
                                                <td>{item.errors.join(', ')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadRecruiterLayout;
